import { useContext, useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'

import { useAnalyticsEvent } from './analyticsEvent'
import { UserContext, AnalyticsContext } from '../context'
import { UpdateUserDetailParams } from '../types'
import { API } from '../API'

export interface UserDetailError {
  full_name: string[]
  time_zone: string[]
  synthetic: string[]
}
export interface UserDetailFieldOnchangeInterface {
  (event: React.ChangeEvent<HTMLInputElement>, fieldName: string): void
}

export interface TimeZoneChangeInterface {
  ({ value: string }): void
}

const defaultErrorsState: UserDetailError = {
  full_name: [],
  time_zone: [],
  synthetic: [],
}

const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

export function useProfileForm({
  onProfileUpdateSuccess,
}: {
  onProfileUpdateSuccess?: () => void
}) {
  // Router.
  const router = useRouter()
  const { redirect } = router.query

  const analyticEvent = useAnalyticsEvent()

  // Global State
  const {
    currentUser,
    fetchCurrentUser,
    updateProfileDetail,
    sendAmplitudeEvent,
  } = useContext(UserContext)
  const { trackProfileCreated } = useContext(AnalyticsContext)

  // State.
  const [loading, setLoading] = useState(false)
  const [userInfo, setUserInfo] = useState<UpdateUserDetailParams>({
    email: '',
    host_bio: '',
    full_name: '',
    nickname: '',
    time_zone: defaultTimezone,
  })
  const [formError, setFormError] = useState<UserDetailError>({
    full_name: [],
    time_zone: [],
    synthetic: [],
  })

  const userDetailFieldOnchange: UserDetailFieldOnchangeInterface = useCallback(
    (event, fieldName) => {
      const eventValue = event.target.value
      const info = { ...userInfo }
      info[fieldName] = eventValue
      if (fieldName === 'full_name') {
        setFormError((formError) => ({ ...formError, full_name: [] }))
      }
      if (fieldName === 'time_zone') {
        setFormError((formError) => ({ ...formError, time: [] }))
      }
      setUserInfo(info)
    },
    [userInfo],
  )
  const timeZoneChange: TimeZoneChangeInterface = useCallback(
    ({ value }) => {
      const nextInfo = { ...userInfo, time_zone: value }
      setUserInfo(nextInfo)

      setFormError((error) => ({ ...error, time_zone: [] }))
    },
    [userInfo],
  )
  // Form Is Submittable.
  const requiredFieldsArePresent = userInfo.full_name && userInfo.time_zone
  const formShouldBeSubmittable = requiredFieldsArePresent && !loading

  const validateDetailInput = useCallback((): boolean => {
    let result = true
    const currentFormError = { ...formError }

    if (!userInfo.full_name || userInfo.full_name === '') {
      result = false
      currentFormError.full_name = ['Required *']
    } else {
      currentFormError.full_name = []
    }

    if (!userInfo.time_zone || userInfo.time_zone === '') {
      result = false
      currentFormError.time_zone = ['Required *']
    } else {
      currentFormError.time_zone = []
    }

    setFormError(currentFormError)
    return result
  }, [formError, userInfo])

  const sendAmplitudeUpdateProfile = () => {
    sendAmplitudeEvent({ type: 'user_update_profile' })
  }

  // On Submit.
  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      // Prevent Default.
      e.preventDefault()

      if (validateDetailInput()) {
        analyticEvent.profileUpdateButtonClicked()
        // Flag Loading.
        setLoading(true)
        setFormError({ ...defaultErrorsState })

        // Force update the user to be a host user
        try {
          await API.get('/rest-auth/registration/convert/')
        } catch (e) {
          setFormError({
            ...defaultErrorsState,
            synthetic: [
              'There was an error saving your profile please try again',
            ],
          })
          setLoading(false)
          return
        }

        // The run an update of the user once it has been converted
        await updateProfileDetail(userInfo)
          .then(() => {
            trackProfileCreated()
            //sendAmplitudeUpdateProfile()
            if (typeof onProfileUpdateSuccess !== 'function') {
              router.replace({
                pathname: (redirect as string) || '/myevents',
                query: {
                  welcome: true,
                },
              })
            }
          })
          .catch((error) => {
            setFormError({ ...defaultErrorsState, ...error.response.data })
          })
          .finally(() => {
            setLoading(false)

            if (typeof onProfileUpdateSuccess === 'function') {
              onProfileUpdateSuccess()
            }
          })
      }
    },
    [
      analyticEvent,
      onProfileUpdateSuccess,
      redirect,
      router,
      trackProfileCreated,
      updateProfileDetail,
      userInfo,
      validateDetailInput,
    ],
  )

  const [isCurrentUserDetailsAdded, setIsCurrentUserDetailsAdded] =
    useState(false)

  useEffect(() => {
    if (currentUser && !isCurrentUserDetailsAdded) {
      setUserInfo({
        full_name: currentUser.full_name
          ? currentUser.full_name
          : userInfo.full_name,
        email: currentUser.email ? currentUser.email : userInfo.email,
        nickname: currentUser.nickname
          ? currentUser.nickname
          : userInfo.nickname,
        host_bio: currentUser.host_bio
          ? currentUser.host_bio
          : userInfo.host_bio,
        time_zone: currentUser.time_zone
          ? currentUser.time_zone
          : userInfo.time_zone,
      })
      setIsCurrentUserDetailsAdded(true)
    }
  }, [currentUser, isCurrentUserDetailsAdded, userInfo])

  useEffect(() => {
    if (currentUser) {
      analyticEvent.fbCompleteRegistration(currentUser.email)
    }
  }, [analyticEvent, currentUser, fetchCurrentUser])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return {
    formError,
    onSubmit,
    userDetailFieldOnchange,
    userInfo,
    timeZoneChange,
    setUserInfo,
    loading,
    currentUser,
  }
}
